import { PREV_COLUMN_SUFFIX, type TileColumn, type TileResult } from "@csbi/contracts"
import { csvCell } from "@csbi/viz-core"

export type ExportTile = { title: string; result: TileResult }

const isMeasure = (c: TileColumn) => c.role === "measure"

function dimensionTitles(tiles: readonly ExportTile[]): string[] {
  const seen: string[] = []
  for (const { result } of tiles) {
    for (const c of result.columns) {
      if (c.role === "dimension" && !seen.includes(c.title)) seen.push(c.title)
    }
  }
  return seen
}

function hasPrevious(tiles: readonly ExportTile[]): boolean {
  return tiles.some(({ result }) =>
    result.columns
      .filter(isMeasure)
      .some((c) => result.rows.some((r) => r[`${c.key}${PREV_COLUMN_SUFFIX}`] != null)),
  )
}

export function toLongCsv(tiles: readonly ExportTile[]): string {
  const dims = dimensionTitles(tiles)
  const withPrev = hasPrevious(tiles)

  const withPeriod = tiles.some(({ result }) => result.columns.some((c) => c.role === "time"))

  const header = [
    "Chart",
    "Metric",
    "Metric ID",
    "Unit",
    ...(withPeriod ? ["Period"] : []),
    ...dims,
    "Value",
    ...(withPrev ? ["Previous value"] : []),
  ]
  const lines = [header.map(csvCell).join(",")]

  for (const { title, result } of tiles) {
    const time = result.columns.find((c) => c.role === "time")
    const dimColumns = result.columns.filter((c) => c.role === "dimension")
    const measures = result.columns.filter(isMeasure)

    for (const row of result.rows) {
      const period = time ? row[time.key] : ""
      const periodCell = withPeriod ? [period] : []
      const dimValues = dims.map((t) => {
        const col = dimColumns.find((c) => c.title === t)
        return col ? row[col.key] : ""
      })

      if (measures.length === 0) {
        lines.push([title, "", "", "", ...periodCell, ...dimValues, "", ...(withPrev ? [""] : [])].map(csvCell).join(","))
        continue
      }

      for (const m of measures) {
        const cells = [
          title,
          m.title,
          m.metricId ?? "",
          m.unit ?? "",
          ...periodCell,
          ...dimValues,
          row[m.key] ?? "",
          ...(withPrev ? [row[`${m.key}${PREV_COLUMN_SUFFIX}`] ?? ""] : []),
        ]
        lines.push(cells.map(csvCell).join(","))
      }
    }
  }

  return lines.join("\n")
}
